import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { MiddlewareEntry } from "@/api/admin-api"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"

import { RawJsonTab } from "./shared/raw-json-tab"

type EditMiddlewareDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  agentName: string
  /** Entry being edited, or null when adding a new one. */
  entry: MiddlewareEntry | null
  /** Called when the Form tab is submitted with a partial/full entry. */
  onSubmit: (next: MiddlewareEntry) => Promise<void>
  /**
   * Called when the Raw tab is confirmed. Returns the full replacement for
   * this specific entry (the parent should route through the single-entry
   * PUT rather than the raw-replace endpoint so the hash check is local).
   */
  onRawSave: (next: MiddlewareEntry, reason: string) => Promise<void>
  isPending?: boolean
}

/**
 * Tabbed dialog for editing (or adding) a single middleware entry.
 *
 * The Form tab is a simple field editor covering id/type/enabled/config. The
 * Raw JSON tab delegates to ``RawJsonTab`` and hands the parsed value back
 * via ``onRawSave``, including the mandatory confirmation reason.
 *
 * Used by ``middleware-table.tsx`` (edit button) and
 * ``system-config-section.tsx`` (add button).
 */
export function EditMiddlewareDialog({
  isOpen,
  onOpenChange,
  agentName,
  entry,
  onSubmit,
  onRawSave,
  isPending = false,
}: EditMiddlewareDialogProps) {
  const { t } = useTranslation()
  const isAdding = entry === null
  const [type, setType] = useState(entry?.type ?? "")
  const [id, setId] = useState(entry?.id ?? "")
  const [enabled, setEnabled] = useState(entry?.enabled ?? true)
  const [configText, setConfigText] = useState(() => JSON.stringify(entry?.config ?? {}, null, 2))
  const [configError, setConfigError] = useState<string | null>(null)
  const [typeError, setTypeError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setType(entry?.type ?? "")
      setId(entry?.id ?? "")
      setEnabled(entry?.enabled ?? true)
      setConfigText(JSON.stringify(entry?.config ?? {}, null, 2))
      setConfigError(null)
      setTypeError(null)
    }
  }, [isOpen, entry])

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setTypeError(null)
    setConfigError(null)

    if (!type.trim()) {
      setTypeError(
        t("adminDashboard.middleware.errorTypeRequired", { defaultValue: "Type is required" })
      )
      return
    }

    let parsedConfig: Record<string, unknown> = {}
    try {
      const parsed = JSON.parse(configText || "{}")
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        throw new Error(
          t("adminDashboard.middleware.errorConfigMustBeObject", {
            defaultValue: "Config must be a JSON object",
          })
        )
      }
      parsedConfig = parsed as Record<string, unknown>
    } catch (err) {
      setConfigError(
        err instanceof Error
          ? err.message
          : t("adminDashboard.middleware.errorInvalidJson", { defaultValue: "Invalid JSON" })
      )
      return
    }

    const next: MiddlewareEntry = {
      id: id.trim() || entry?.id || "",
      type: type.trim(),
      enabled,
      config: parsedConfig,
    }

    await onSubmit(next)
  }

  const rawInitialValue: MiddlewareEntry = entry ?? {
    id: id.trim() || "",
    type: type.trim(),
    enabled,
    config: {},
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>
            {isAdding
              ? t("adminDashboard.middleware.dialogAddTitle", { defaultValue: "Add middleware" })
              : t("adminDashboard.middleware.dialogEditTitle", {
                  defaultValue: "Edit middleware",
                })}{" "}
            <span className="text-muted-foreground text-sm font-normal">({agentName})</span>
          </DialogTitle>
          <DialogDescription>
            {isAdding
              ? t("adminDashboard.middleware.dialogAddDescription", {
                  defaultValue: "Append a new middleware entry to the agent's chain.",
                })
              : t("adminDashboard.middleware.dialogEditDescription", {
                  defaultValue:
                    "Edit this middleware entry. Use the Raw JSON tab for fields not covered by the form.",
                })}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="form">
          <TabsList>
            <TabsTrigger value="form">
              {t("adminDashboard.middleware.tabForm", { defaultValue: "Form" })}
            </TabsTrigger>
            {entry !== null ? (
              <TabsTrigger
                className="text-destructive data-[state=active]:text-destructive"
                value="raw"
              >
                {t("adminDashboard.middleware.tabRaw", { defaultValue: "Raw JSON" })}
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent className="mt-4" value="form">
            <form className="space-y-4" onSubmit={handleFormSubmit}>
              <div className="space-y-2">
                <Label htmlFor="mw-type">
                  {t("adminDashboard.middleware.fieldType", { defaultValue: "Type" })}{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="mw-type"
                  value={type}
                  placeholder={t("adminDashboard.middleware.fieldTypePlaceholder", {
                    defaultValue: "e.g. logging, rate_limit",
                  })}
                  onChange={(event) => setType(event.target.value)}
                />
                {typeError ? <p className="text-destructive text-xs">{typeError}</p> : null}
              </div>

              {!isAdding ? (
                <div className="space-y-2">
                  <Label htmlFor="mw-id">
                    {t("adminDashboard.middleware.fieldId", { defaultValue: "ID" })}
                  </Label>
                  <Input disabled className="font-mono text-xs" id="mw-id" value={id} />
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.middleware.fieldIdHelp", {
                      defaultValue:
                        "IDs are immutable after creation. Use Raw mode + audit trail if you truly need to re-key.",
                    })}
                  </p>
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="mw-enabled">
                    {t("adminDashboard.middleware.fieldEnabled", { defaultValue: "Enabled" })}
                  </Label>
                  <p className="text-muted-foreground text-xs">
                    {t("adminDashboard.middleware.fieldEnabledHelp", {
                      defaultValue: "Disabled entries stay in the list but are skipped at runtime.",
                    })}
                  </p>
                </div>
                <Switch checked={enabled} id="mw-enabled" onCheckedChange={setEnabled} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mw-config">
                  {t("adminDashboard.middleware.fieldConfig", { defaultValue: "Config (JSON)" })}
                </Label>
                <Textarea
                  className="font-mono text-xs"
                  id="mw-config"
                  rows={8}
                  spellCheck={false}
                  value={configText}
                  onChange={(event) => setConfigText(event.target.value)}
                />
                {configError ? <p className="text-destructive text-xs">{configError}</p> : null}
              </div>

              <DialogFooter>
                <Button
                  disabled={isPending}
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  {t("adminDashboard.middleware.cancel", { defaultValue: "Cancel" })}
                </Button>
                <Button disabled={isPending} type="submit">
                  {(() => {
                    if (isPending) {
                      return (
                        <>
                          <Spinner />
                          {t("adminDashboard.middleware.saving", { defaultValue: "Saving…" })}
                        </>
                      )
                    }
                    return isAdding
                      ? t("adminDashboard.middleware.add", { defaultValue: "Add" })
                      : t("adminDashboard.middleware.save", { defaultValue: "Save" })
                  })()}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          {entry !== null ? (
            <TabsContent className="mt-4" value="raw">
              <RawJsonTab
                confirmToken={entry.id}
                isPending={isPending}
                value={rawInitialValue}
                resourceLabel={t("adminDashboard.middleware.rawResourceLabel", {
                  defaultValue: "middleware entry {{id}}",
                  id: entry.id,
                })}
                onSave={async (next, reason) => {
                  await onRawSave(next as MiddlewareEntry, reason)
                }}
              />
            </TabsContent>
          ) : null}
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
